<template>
  <div class="ca-page">
    <div class="ca-body">
      <!-- Toolbar -->
      <div class="ca-toolbar">
        <div class="ca-toolbar-title">风控告警</div>
        <div class="ca-toolbar-spacer"></div>
        <button class="ca-export-btn" @click="exportPdf">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9V2h12v7"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><path d="M6 14h12v8H6z"/></svg>
          导出 PDF 报告
        </button>
      </div>
      <!-- KPI Row -->
      <div class="ca-kpi-row">
        <div v-for="kpi in kpiList" :key="kpi.label" class="ca-kpi-card">
          <div class="ca-kpi-icon-wrap" :style="{ background: kpi.iconBg }">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" :stroke="kpi.iconColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path v-if="kpi.icon === 'alert-triangle'" d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z M12 9v4 M12 17h.01" />
              <path v-else-if="kpi.icon === 'alert-circle'" d="M12 2a10 10 0 100 20 10 10 0 000-20z M12 8v4 M12 16h.01" />
              <path v-else-if="kpi.icon === 'server'" d="M2 5a2 2 0 012-2h16a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2V5z M2 15a2 2 0 012-2h16a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z M6 7h.01 M6 19h.01" />
              <path v-else-if="kpi.icon === 'file-text'" d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z M14 2v6h6 M16 13H8 M16 17H8 M10 9H8" />
              <path v-else-if="kpi.icon === 'refresh'" d="M23 4v6h-6 M1 20v-6h6 M3.51 9a9 9 0 0114.85-3.36L23 10 M20.49 15a9 9 0 01-14.85 3.36L1 14" />
            </svg>
          </div>
          <div>
            <div class="ca-kpi-label">{{ kpi.label }}</div>
            <div class="ca-kpi-value" :style="{ color: kpi.valueColor }">{{ kpi.value }}</div>
          </div>
        </div>
      </div>

      <!-- AI 风控研判面板 -->
      <div class="ca-ai-panel">
        <div class="ca-ai-inner">
          <div class="ca-ai-head">
            <span class="ca-ai-spark">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l1.8 5.2L19 9l-5.2 1.8L12 16l-1.8-5.2L5 9l5.2-1.8L12 2zm7 11l.9 2.6L22 16l-2.1.9L19 19l-.9-2.1L16 16l2.1-.9L19 13zM5 14l.9 2.6L8 17l-2.1.9L5 20l-.9-2.1L2 17l2.1-.4L5 14z"/></svg>
            </span>
            <span class="ca-ai-title">AI 风控研判</span>
            <span class="ca-ai-badge">GPT-5.5</span>
            <span style="flex:1"></span>
            <button class="ca-ai-btn" :disabled="aiLoading" @click="loadAiReport">
              {{ aiLoading ? '分析中…' : (aiReport ? '重新生成' : '生成 AI 研判') }}
            </button>
          </div>
          <div v-if="aiLoading" class="ca-ai-loading">正在调用 GPT 分析本期风控指标，请稍候…</div>
          <div v-else-if="aiError" class="ca-ai-error">{{ aiError }}</div>
          <div v-else-if="aiReport">
            <div class="ca-ai-body" v-if="aiReport.summary">{{ aiReport.summary }}</div>
            <div class="ca-ai-body ca-ai-raw" v-else-if="aiReport.raw">{{ aiReport.raw }}</div>
            <div class="ca-ai-verdicts" v-if="(aiReport.verdicts && aiReport.verdicts.length) || aiReport.riskLevel">
              <span v-if="aiReport.riskLevel" class="ca-ai-chip">整体风险：<b :class="aiRiskClass">{{ aiRiskText }}</b></span>
              <span v-for="(v, i) in (aiReport.verdicts || [])" :key="i" class="ca-ai-chip">{{ v.label }}：<b>{{ v.value }}</b></span>
            </div>
            <ul class="ca-ai-recs" v-if="aiReport.recommendations && aiReport.recommendations.length">
              <li v-for="(r, i) in aiReport.recommendations" :key="i">{{ r }}</li>
            </ul>
            <div class="ca-ai-note">* 由 GPT-5.5 基于本期指标生成，仅供风控参考，最终以人工复核为准。</div>
          </div>
          <div v-else class="ca-ai-empty">点击「生成 AI 研判」，让 GPT 分析本期费用异常、大实例开通与账号频繁进出，给出整体风险等级与处置建议。</div>
        </div>
      </div>

      <!-- Tabs -->
      <el-tabs v-model="activeTab" class="ca-tabs">
        <el-tab-pane name="cost-anomaly">
          <template #label>
            <span>费用异常</span>
            <span v-if="criticalAlerts > 0" class="ca-tab-badge ca-badge-red">{{ criticalAlerts }}</span>
          </template>
        </el-tab-pane>
        <el-tab-pane name="large-instance">
          <template #label>
            <span>大实例开通</span>
            <span v-if="newInstances > 0" class="ca-tab-badge ca-badge-orange">{{ newInstances }}</span>
          </template>
        </el-tab-pane>
        <el-tab-pane v-if="!isLimited" name="by-partner" label="按伙伴汇总" />
        <el-tab-pane name="change-chart" label="变化幅度图" />
        <el-tab-pane name="bill-lifecycle">
          <template #label>
            <span>账单生命周期</span>
            <span v-if="pendingBills > 0" class="ca-tab-badge ca-badge-blue">{{ pendingBills }}</span>
          </template>
        </el-tab-pane>
        <el-tab-pane name="frequent-transfer">
          <template #label>
            <span>频繁变更</span>
            <span v-if="frequentChurn > 0" class="ca-tab-badge ca-badge-red">{{ frequentChurn }}</span>
          </template>
        </el-tab-pane>
      </el-tabs>

      <!-- Shared Filters -->
      <div class="ca-filters">
        <div class="ca-search-wrap">
          <svg class="ca-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
          <input v-model="searchQuery" type="text" placeholder="搜索..." class="ca-search-input" />
        </div>
        <el-select v-if="!isLimited" v-model="partnerFilter" filterable size="small" class="ca-filter-select" placeholder="全部伙伴">
          <el-option value="all" label="全部伙伴" />
          <el-option v-for="p in partnerNames" :key="p" :value="p" :label="p" />
        </el-select>
        <el-select v-if="activeTab === 'cost-anomaly' || activeTab === 'large-instance' || activeTab === 'frequent-transfer'" v-model="severityFilter" filterable size="small" class="ca-filter-select ca-filter-sm" placeholder="全部级别">
          <el-option value="all" label="全部级别" />
          <el-option value="critical" label="严重" />
          <el-option value="warning" label="警告" />
          <el-option value="info" label="提示" />
        </el-select>
        <el-select v-if="activeTab === 'large-instance'" v-model="instanceStatusFilter" filterable size="small" class="ca-filter-select ca-filter-sm" placeholder="全部状态">
          <el-option value="all" label="全部状态" />
          <el-option value="new" label="新开通" />
          <el-option value="existing" label="持续运行" />
        </el-select>
        <template v-if="activeTab === 'bill-lifecycle'">
          <el-select v-model="billMonthFilter" filterable size="small" class="ca-filter-select ca-filter-sm" placeholder="全部月份">
            <el-option value="all" label="全部月份" />
            <el-option v-for="m in billMonths" :key="m" :value="m" :label="m" />
          </el-select>
          <el-select v-model="billStatusFilter" filterable size="small" class="ca-filter-select ca-filter-sm" placeholder="全部状态">
            <el-option value="all" label="全部状态" />
            <el-option v-for="(v, k) in billStatusConfig" :key="k" :value="k" :label="v.label" />
          </el-select>
        </template>
      </div>

      <!-- ===== Tab: Cost Anomaly ===== -->
      <div v-if="activeTab === 'cost-anomaly'" class="ca-card">
        <div class="ca-card-header">
          <h3 class="ca-card-title">费用异常告警 ({{ filteredAlerts.length }})</h3>
        </div>
        <div class="ca-divide-y">
          <div v-for="alert in pagedAlerts" :key="alert.id">
            <button class="ca-alert-row" @click="toggleExpand(alert.id)">
              <svg :class="['ca-chevron', { 'ca-chevron-down': expandedIds[alert.id] }]" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>
              <span :class="['ca-sev-badge', sevClass(alert.severity)]">{{ sevLabel(alert.severity) }}</span>
              <div class="ca-alert-info">
                <span class="ca-alert-link">{{ alert.linkName }}</span>
                <span class="ca-alert-meta">· {{ alert.payerName }}</span>
              </div>
              <div :class="['ca-alert-change', alert.type === 'spike' ? 'ca-text-red' : 'ca-text-green']">
                <svg v-if="alert.type === 'spike'" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 17L17 7 M17 7h-6 M17 7v6"/></svg>
                <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 7l10 10 M17 17h-6 M17 17V11"/></svg>
                {{ alert.type === 'spike' ? '+' : '' }}{{ alert.changePercent.toFixed(1) }}%
              </div>
              <div class="ca-alert-date">{{ alert.detectedAt }}</div>
            </button>
            <div v-if="expandedIds[alert.id]" class="ca-alert-detail">
              <div class="ca-detail-grid">
                <div><div class="ca-detail-label">当前费用</div><div class="ca-detail-value">${{ alert.currentSpend.toLocaleString() }}</div></div>
                <div><div class="ca-detail-label">上期费用</div><div class="ca-detail-value">${{ alert.previousSpend.toLocaleString() }}</div></div>
                <div v-if="!isLimited"><div class="ca-detail-label">伙伴</div><div class="ca-detail-value ca-detail-text">{{ alert.partnerName }}</div></div>
                <div><div class="ca-detail-label">Link ID</div><div class="ca-detail-value ca-text-primary">{{ alert.linkId }}</div></div>
              </div>
              <p class="ca-detail-desc">{{ alert.description }}</p>
            </div>
          </div>
          <div v-if="filteredAlerts.length === 0" class="ca-empty">暂无匹配的告警</div>
        </div>
        <div v-if="filteredAlerts.length > anomalyPageSize" class="ca-pagination">
          <el-pagination
            v-model:current-page="anomalyPage"
            :page-size="anomalyPageSize"
            :total="filteredAlerts.length"
            layout="total, prev, pager, next"
            small background />
        </div>
      </div>

      <!-- ===== Tab: Large Instance ===== -->
      <div v-if="activeTab === 'large-instance'" class="ca-card">
        <div class="ca-card-header ca-card-header-between">
          <h3 class="ca-card-title">大实例开通告警 ({{ instTotal }})</h3>
          <span class="ca-card-subtitle">检测到超大规格实例（{{ instMonthLabel }}，"新开通"=该资源上月未出现）</span>
        </div>
        <div class="ca-table-wrap" v-loading="instLoading">
          <table class="ca-table">
            <thead><tr><th v-for="h in instanceHeaders" :key="h">{{ h }}</th></tr></thead>
            <tbody>
              <tr v-for="inst in instancesData" :key="inst.id" class="ca-table-row">
                <td class="ca-td-primary ca-font-data" :title="inst.instanceId">{{ inst.instanceId.slice(0, 18) }}{{ inst.instanceId.length > 18 ? '...' : '' }}</td>
                <td class="ca-td-bold ca-font-data">{{ inst.instanceType }}</td>
                <td class="ca-td-muted">{{ inst.service }}</td>
                <td class="ca-td-muted">{{ inst.region }}</td>
                <td class="ca-td-primary">{{ inst.linkName }}</td>
                <td class="ca-td-muted">{{ inst.payerName }}</td>
                <td v-if="!isLimited" class="ca-td-muted">{{ (inst.partnerName || '').split(' ')[0] }}</td>
                <td class="ca-td-muted ca-nowrap">{{ inst.launchTime || '—' }}</td>
                <td class="ca-td-bold ca-font-data">${{ inst.hourlyCost.toFixed(3) }}/hr</td>
                <td class="ca-td-red ca-font-data">${{ inst.estimatedMonthlyCost.toLocaleString() }}/mo</td>
                <td><span :class="['ca-sev-badge', instStatusClass(inst.status)]">{{ instStatusLabel(inst.status) }}</span></td>
                <td><span :class="['ca-sev-badge', sevClass(inst.severity)]">{{ sevLabel(inst.severity) }}</span></td>
              </tr>
              <tr v-if="instancesData.length === 0 && !instLoading"><td :colspan="isLimited ? 11 : 12" class="ca-empty">暂无匹配的告警</td></tr>
            </tbody>
          </table>
        </div>
        <div v-if="instTotal > instPageSize" class="ca-pagination">
          <el-pagination
            v-model:current-page="instPage"
            :page-size="instPageSize"
            :total="instTotal"
            layout="total, prev, pager, next"
            small background
            @current-change="loadInstances" />
        </div>
      </div>

      <!-- ===== Tab: By Partner ===== -->
      <div v-if="activeTab === 'by-partner'" class="ca-partner-grid">
        <div class="ca-card ca-card-pad">
          <h3 class="ca-card-title ca-mb12">各伙伴告警数量</h3>
          <div ref="partnerChartRef" style="width:100%;height:220px"></div>
        </div>
        <div class="ca-card">
          <div class="ca-card-header"><h3 class="ca-card-title">伙伴告警汇总</h3></div>
          <div class="ca-divide-y">
            <div v-for="ps in partnerSummary" :key="ps.name" class="ca-partner-row">
              <div class="ca-partner-info">
                <div class="ca-partner-name">{{ ps.name }}</div>
                <div class="ca-partner-count">共 {{ ps.totalAlerts }} 条告警</div>
              </div>
              <div class="ca-partner-badges">
                <span v-if="ps.critical > 0" class="ca-sev-badge ca-sev-critical">{{ ps.critical }}</span>
                <span v-if="ps.warning > 0" class="ca-sev-badge ca-sev-warning">{{ ps.warning }}</span>
                <span v-if="ps.info > 0" class="ca-sev-badge ca-sev-info">{{ ps.info }}</span>
                <span v-if="ps.newInstances > 0" class="ca-sev-badge ca-sev-orange">{{ ps.newInstances }} 大实例</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== Tab: Change Chart ===== -->
      <div v-if="activeTab === 'change-chart'" class="ca-card ca-card-pad">
        <h3 class="ca-card-title">费用变化幅度</h3>
        <p class="ca-card-subtitle ca-mb16">各 Link 账号费用环比变化幅度（正值为增长，负值为下降）</p>
        <div ref="changeChartRef" style="width:100%;height:320px"></div>
        <div class="ca-legend-row">
          <div class="ca-legend-item"><div class="ca-legend-dot" style="background:#c05c5c"></div><span>费用增长</span></div>
          <div class="ca-legend-item"><div class="ca-legend-dot" style="background:#3d9e7a"></div><span>费用下降</span></div>
        </div>
      </div>

      <!-- ===== Tab: Bill Lifecycle ===== -->
      <template v-if="activeTab === 'bill-lifecycle'">
        <div class="ca-card ca-card-pad">
          <h3 class="ca-card-title ca-mb12">账单流转状态概览</h3>
          <div class="ca-pipeline">
            <template v-for="(step, i) in BILL_STEPS" :key="step">
              <div class="ca-pipeline-step">
                <div :class="['ca-pipeline-box', pipelineCount(i) > 0 ? 'ca-pipeline-active' : 'ca-pipeline-inactive']">
                  <div :class="['ca-pipeline-num', pipelineCount(i) > 0 ? 'ca-text-primary' : 'ca-text-muted']">{{ pipelineCount(i) }}</div>
                  <div class="ca-pipeline-label">{{ step }}</div>
                </div>
              </div>
              <svg v-if="i < BILL_STEPS.length - 1" class="ca-pipeline-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#71717a" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>
            </template>
          </div>
        </div>
        <div class="ca-card">
          <div class="ca-card-header"><h3 class="ca-card-title">账单列表 ({{ billTotal }})</h3></div>
          <div class="ca-table-wrap" v-loading="billLoading">
            <table class="ca-table">
              <thead><tr><th v-for="h in billHeaders" :key="h">{{ h }}</th></tr></thead>
              <tbody>
                <tr v-for="bill in billsData" :key="bill.id" class="ca-table-row">
                  <td class="ca-td-primary ca-font-data">{{ bill.id }}</td>
                  <td class="ca-td-bold">{{ bill.billMonth }}</td>
                  <td v-if="!isLimited" class="ca-td-muted">{{ (bill.partnerName || '').split(' ')[0] }}</td>
                  <td class="ca-td-muted">{{ bill.payerName }}</td>
                  <td class="ca-td-muted ca-font-data">{{ bill.linkId || '—' }}</td>
                  <td class="ca-td-bold ca-font-data">${{ bill.charge != null ? bill.charge.toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2}) : '0.00' }}</td>
                  <td class="ca-td-muted ca-font-data">${{ bill.cost != null ? bill.cost.toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2}) : '0.00' }}</td>
                  <td>
                    <el-dropdown trigger="click" @command="(cmd) => onStatusChange(bill, cmd)">
                      <span :class="['ca-sev-badge ca-status-clickable', billStatusCls(bill.status)]">{{ billStatusLabel(bill.status) }} ▾</span>
                      <template #dropdown>
                        <el-dropdown-menu>
                          <el-dropdown-item v-for="(v, k) in billStatusConfig" :key="k" :command="k" :disabled="k === bill.status">{{ v.label }}</el-dropdown-item>
                        </el-dropdown-menu>
                      </template>
                    </el-dropdown>
                  </td>
                  <td class="ca-td-muted ca-td-truncate" :title="bill.notes">{{ bill.notes || '—' }}</td>
                  <td>
                    <button v-if="nextAction(bill.status)" class="ca-action-btn" @click="doBillAction(bill)">{{ nextAction(bill.status).label }}</button>
                    <span v-else class="ca-td-muted">—</span>
                  </td>
                </tr>
                <tr v-if="billsData.length === 0 && !billLoading"><td :colspan="isLimited ? 9 : 10" class="ca-empty">暂无匹配的账单</td></tr>
              </tbody>
            </table>
          </div>
          <div v-if="billTotal > billPageSize" class="ca-pagination">
            <el-pagination
              v-model:current-page="billCurrentPage"
              :page-size="billPageSize"
              :total="billTotal"
              layout="total, prev, pager, next"
              small background
              @current-change="loadBills" />
          </div>
        </div>
        <div class="ca-lifecycle-grid">
          <div v-for="bill in nonArchivedBills" :key="bill.id" class="ca-lifecycle-card">
            <div class="ca-lc-header">
              <div>
                <div class="ca-lc-month">{{ bill.billMonth }}</div>
                <div class="ca-lc-partner"><template v-if="!isLimited">{{ (bill.partnerName || '').split(' ')[0] }} · </template>{{ bill.payerName }}</div>
              </div>
              <span :class="['ca-sev-badge', billStatusCls(bill.status)]">{{ billStatusLabel(bill.status) }}</span>
            </div>
            <div class="ca-lc-amounts">
              <div><div class="ca-detail-label">Charge</div><div class="ca-lc-amount">${{ (bill.charge || 0).toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2}) }}</div></div>
              <div class="ca-text-right"><div class="ca-detail-label">Cost</div><div class="ca-lc-due">${{ (bill.cost || 0).toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2}) }}</div></div>
            </div>
            <div class="ca-lc-progress">
              <div v-for="(step, i) in BILL_STEPS" :key="step" class="ca-lc-step">
                <div :class="['ca-lc-bar', i < billStep(bill.status) ? 'ca-lc-bar-done' : i === billStep(bill.status) ? 'ca-lc-bar-current' : 'ca-lc-bar-pending']"></div>
                <div v-if="i === billStep(bill.status)" class="ca-lc-step-label">{{ step }}</div>
              </div>
            </div>
            <div v-if="nextAction(bill.status)" class="ca-lc-action">
              <button class="ca-action-btn" @click="doBillAction(bill)">{{ nextAction(bill.status).label }}</button>
            </div>
            <div v-if="bill.notes" class="ca-lc-notes">{{ bill.notes }}</div>
          </div>
        </div>
      </template>

      <!-- ===== Tab: Frequent Transfer ===== -->
      <div v-if="activeTab === 'frequent-transfer'" class="ca-card" v-loading="freqLoading">
        <div class="ca-card-header ca-card-header-between">
          <h3 class="ca-card-title">频繁变更账号告警 ({{ filteredFreqPayers.length }})</h3>
          <span class="ca-card-subtitle">检测最近 12 个月内 link 频繁解绑/重绑（短命=存活≤2个月，{{ freqMaxMonth ? String(freqMaxMonth).slice(0,4)+'-'+String(freqMaxMonth).slice(4) : '—' }}）</span>
        </div>
        <div class="ca-divide-y">
          <div v-for="payer in filteredFreqPayers" :key="payer.payerId">
            <button class="ca-alert-row" @click="toggleFreqExpand(payer.payerId)">
              <svg :class="['ca-chevron', { 'ca-chevron-down': freqExpandedIds[payer.payerId] }]" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>
              <span :class="['ca-sev-badge', sevClass(payer.severity)]">{{ sevLabel(payer.severity) }}</span>
              <div class="ca-alert-info">
                <span class="ca-alert-link">{{ payer.payerName }}</span>
                <span v-if="!isLimited" class="ca-alert-meta">· {{ (payer.partnerName || '').split(' ')[0] || '未分配' }}<span v-if="payer.salesName"> · {{ payer.salesName }}</span></span>
              </div>
              <div class="ca-alert-change ca-text-red">
                <span>短命 {{ payer.shortLived }}/{{ payer.totalLinks }} ({{ Math.round((payer.shortRatio || 0) * 100) }}%)</span>
              </div>
              <div class="ca-alert-date">均寿 {{ (payer.avgLifespan || 0).toFixed(1) }} 月</div>
            </button>
            <div v-if="freqExpandedIds[payer.payerId]" class="ca-alert-detail">
              <div class="ca-detail-grid">
                <div><div class="ca-detail-label">总 Link 数</div><div class="ca-detail-value">{{ payer.totalLinks }}</div></div>
                <div><div class="ca-detail-label">短命 Link 数</div><div class="ca-detail-value ca-text-red">{{ payer.shortLived }}</div></div>
                <div><div class="ca-detail-label">短命占比</div><div class="ca-detail-value ca-text-red">{{ Math.round((payer.shortRatio || 0) * 100) }}%</div></div>
                <div><div class="ca-detail-label">月最大变更</div><div class="ca-detail-value">{{ payer.maxMonthlyChurn || 0 }}</div></div>
              </div>
              <div v-if="(payer.shortLivedLinks || []).length" class="ca-table-wrap" style="margin-top:8px">
                <table class="ca-table">
                  <thead><tr><th>Link ID</th><th>Link 名称</th><th>首次月</th><th>末次月</th><th>活跃月数</th></tr></thead>
                  <tbody>
                    <tr v-for="l in payer.shortLivedLinks" :key="l.linkId" class="ca-table-row">
                      <td class="ca-td-primary ca-font-data">{{ l.linkId }}</td>
                      <td class="ca-td-bold">{{ l.linkName || '—' }}</td>
                      <td class="ca-td-muted ca-font-data">{{ l.firstMonth }}</td>
                      <td class="ca-td-muted ca-font-data">{{ l.lastMonth }}</td>
                      <td class="ca-td-bold ca-font-data">{{ l.activeMonths }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div v-if="!freqLoading && filteredFreqPayers.length === 0" class="ca-empty">暂无频繁变更告警</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { ajaxGet, ajaxPut, ajaxPost } from '@/api/request'
import { buildReportHtml } from './reportTemplate'

const BILL_STEPS = ['待确认', '已确认', '已开票', '已付款', '已归档']

const billStatusConfig = {
  pending_confirm: { label: '待确认', step: 0 },
  confirmed:       { label: '已确认', step: 1 },
  invoiced:        { label: '已开票', step: 2 },
  paid:            { label: '已付款', step: 3 },
  archived:        { label: '已归档', step: 4 },
}

export default {
  name: 'CostAlerts',
  data() {
    return {
      activeTab: 'cost-anomaly',
      severityFilter: 'all',
      partnerFilter: 'all',
      instanceStatusFilter: 'new',
      billMonthFilter: (() => { const d = new Date(); d.setMonth(d.getMonth() - 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` })(),
      billStatusFilter: 'all',
      searchQuery: '',
      expandedIds: {},
      billCurrentPage: 1,
      billPageSize: 20,
      billTotal: 0,
      billLoading: false,
      anomalyPage: 1,
      anomalyPageSize: 20,
      instPage: 1,
      instPageSize: 20,
      instTotal: 0,
      instMonth: null,
      instPrevMonth: null,
      instLoading: false,
      BILL_STEPS,
      billStatusConfig,
      partnerChartInstance: null,
      changeChartInstance: null,
      alertsData: [],
      instancesData: [],
      partnerNames: [],
      partnerSummaryData: [],
      billsData: [],
      billMonthsServer: [],
      billPipeline: {},
      kpiData: { critical: 0, warning: 0, newInstances: 0, pendingBills: 0, frequentChurn: 0 },
      freqData: [],
      freqMaxMonth: null,
      freqLoading: false,
      freqExpandedIds: {},
      aiLoading: false,
      aiReport: null,
      aiError: '',
    }
  },
  computed: {
    isLimited() {
      const ui = JSON.parse(localStorage.getItem('userInfo') || '{}')
      const t = Number(ui.type) || 0
      return t === 2 || t === 4
    },
    instanceHeaders() {
      const h = ['实例ID', '实例类型', '服务', '区域', 'Link', 'Payer', '伙伴', '首次出现', '小时费用', '月度预估', '状态', '级别']
      return this.isLimited ? h.filter(x => x !== '伙伴') : h
    },
    billHeaders() {
      const h = ['账单ID', '账单月份', '伙伴', 'Payer', 'LinkId', 'Charge', 'Cost', '状态', '备注', '操作']
      return this.isLimited ? h.filter(x => x !== '伙伴') : h
    },
    criticalAlerts() { return this.kpiData.critical },
    warningAlerts() { return this.kpiData.warning },
    newInstances() { return this.kpiData.newInstances },
    pendingBills() { return this.kpiData.pendingBills },
    frequentChurn() { return this.kpiData.frequentChurn || 0 },
    aiRiskText() {
      return { high: '高', medium: '中', low: '低' }[this.aiReport && this.aiReport.riskLevel] || '—'
    },
    aiRiskClass() {
      return { high: 'ca-ai-risk-high', medium: 'ca-ai-risk-mid', low: 'ca-ai-risk-low' }[this.aiReport && this.aiReport.riskLevel] || ''
    },
    instMonthLabel() {
      if (!this.instMonth) return '—'
      const m = String(this.instMonth)
      return m.length === 6 ? `${m.slice(0, 4)}-${m.slice(4)}` : m
    },
    kpiList() {
      return [
        { label: '严重告警', value: this.criticalAlerts, valueColor: '#dc2626', icon: 'alert-triangle', iconColor: '#ef4444', iconBg: 'rgba(239,68,68,0.08)' },
        { label: '警告', value: this.warningAlerts, valueColor: '#d97706', icon: 'alert-circle', iconColor: '#f59e0b', iconBg: 'rgba(245,158,11,0.08)' },
        { label: '新发现大实例', value: this.newInstances, valueColor: '#ea580c', icon: 'server', iconColor: '#f97316', iconBg: 'rgba(249,115,22,0.08)' },
        { label: '账单待处理', value: this.pendingBills, valueColor: '#2563eb', icon: 'file-text', iconColor: '#3b82f6', iconBg: 'rgba(59,130,246,0.08)' },
        { label: '频繁变更账号', value: this.frequentChurn, valueColor: '#dc2626', icon: 'refresh', iconColor: '#ef4444', iconBg: 'rgba(239,68,68,0.08)' },
      ]
    },
    filteredAlerts() {
      return this.alertsData.filter(a => {
        if (this.severityFilter !== 'all' && a.severity !== this.severityFilter) return false
        if (this.partnerFilter !== 'all' && a.partnerName !== this.partnerFilter) return false
        if (this.searchQuery) {
          const q = this.searchQuery.toLowerCase()
          if (!(a.linkName || '').toLowerCase().includes(q) && !(a.service || '').toLowerCase().includes(q) && !(a.partnerName || '').toLowerCase().includes(q)) return false
        }
        return true
      })
    },
    pagedAlerts() {
      const start = (this.anomalyPage - 1) * this.anomalyPageSize
      return this.filteredAlerts.slice(start, start + this.anomalyPageSize)
    },
    nonArchivedBills() {
      return this.billsData.filter(b => b.status !== 'archived')
    },
    filteredFreqPayers() {
      return (this.freqData || []).filter(p => {
        if (this.severityFilter !== 'all' && p.severity !== this.severityFilter) return false
        if (this.partnerFilter !== 'all' && p.partnerName !== this.partnerFilter) return false
        if (this.searchQuery) {
          const q = this.searchQuery.toLowerCase()
          const hit = (p.payerName || '').toLowerCase().includes(q)
            || (p.payerId || '').toLowerCase().includes(q)
            || (p.partnerName || '').toLowerCase().includes(q)
            || (p.shortLivedLinks || []).some(l => (l.linkId || '').toLowerCase().includes(q) || (l.linkName || '').toLowerCase().includes(q))
          if (!hit) return false
        }
        return true
      })
    },
    billMonths() { return this.billMonthsServer },
    partnerSummary() { return this.partnerSummaryData },
    changeChartData() {
      return this.filteredAlerts
        .map(a => ({
          name: a.linkName || a.linkId,
          change: Number(a.changePercent) || 0,
          service: a.service,
          fill: a.changePercent > 0 ? '#c05c5c' : '#3d9e7a',
        }))
        .sort((a, b) => Math.abs(b.change) - Math.abs(a.change))
        .slice(0, 30)
    },
  },
  watch: {
    activeTab(val) {
      this.$nextTick(() => {
        if (val === 'by-partner') this.initPartnerChart()
        if (val === 'change-chart') this.initChangeChart()
        if (val === 'frequent-transfer' && !this.freqData.length) this.loadFreq()
      })
    },
    // Large-instance filters → reload from server (server-side filter + pagination)
    instanceStatusFilter() { if (this.activeTab === 'large-instance') { this.instPage = 1; this.loadInstances() } },
    searchQuery() {
      this.anomalyPage = 1
      if (this.activeTab === 'large-instance') { this.instPage = 1; this._debouncedLoadInstances() }
      if (this.activeTab === 'change-chart') this.$nextTick(() => this.initChangeChart())
    },
    severityFilter() {
      this.anomalyPage = 1
      if (this.activeTab === 'large-instance') { this.instPage = 1; this.loadInstances() }
      if (this.activeTab === 'change-chart') this.$nextTick(() => this.initChangeChart())
    },
    partnerFilter() {
      this.anomalyPage = 1
      if (this.activeTab === 'large-instance') { this.instPage = 1; this.loadInstances() }
      if (this.activeTab === 'bill-lifecycle') { this.billCurrentPage = 1; this.loadBills() }
      if (this.activeTab === 'by-partner') this.$nextTick(() => this.initPartnerChart())
      if (this.activeTab === 'change-chart') this.$nextTick(() => this.initChangeChart())
    },
    billMonthFilter() { this.billCurrentPage = 1; this.loadBills() },
    billStatusFilter() { this.billCurrentPage = 1; this.loadBills() },
  },
  methods: {
    async loadAllData() {
      await Promise.allSettled([
        this.loadKpi(), this.loadAlerts(),
        this.loadPartners(), this.loadPartnerSummary(),
        this.loadInstances(), this.loadBills(),
        this.loadFreq(),
      ])
    },
    async loadKpi() {
      const res = await ajaxGet({ url: '/stat/alerts/kpi/' })
      if (res && res.data) this.kpiData = res.data
    },
    async loadAiReport() {
      this.aiLoading = true
      this.aiError = ''
      try {
        const res = await ajaxPost({ url: '/stat/alerts/ai_report/' })
        if (res && res.code === 2000 && res.data) {
          this.aiReport = res.data
        } else {
          this.aiError = (res && res.msg) || 'AI 研判生成失败'
        }
      } catch (e) {
        this.aiError = 'AI 研判请求失败：' + ((e && e.message) ? e.message : e)
      } finally {
        this.aiLoading = false
      }
    },
    buildReportPayload() {
      const now = new Date()
      const pad = n => String(n).padStart(2, '0')
      const genAt = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`
      // 费用异常 Top 15（按环比绝对值）
      const anomalies = [...this.alertsData]
        .sort((a, b) => Math.abs(Number(b.changePercent) || 0) - Math.abs(Number(a.changePercent) || 0))
        .slice(0, 15)
      // 严重度构成
      const severityMix = { critical: 0, warning: 0, info: 0 }
      this.alertsData.forEach(a => { if (severityMix[a.severity] != null) severityMix[a.severity]++ })
      // 按伙伴异常费用合计 Top 6
      const cats = ['#8456ce', '#2ea597', '#e07941', '#688ae8', '#c33d69', '#67a353']
      const pmap = {}
      this.alertsData.forEach(a => {
        const k = (a.partnerName || '').split(' ')[0] || '未分配'
        pmap[k] = (pmap[k] || 0) + (Number(a.currentSpend) || 0)
      })
      let partnerBars = Object.keys(pmap).map(name => ({ name, value: pmap[name] }))
        .sort((a, b) => b.value - a.value).slice(0, 6)
      const maxv = partnerBars.length ? (partnerBars[0].value || 1) : 1
      partnerBars = partnerBars.map((b, i) => ({ ...b, pct: Math.max(3, Math.round(b.value / maxv * 100)), color: cats[i % cats.length] }))
      // 大实例 Top 12（后端已按成本降序）
      const instSlice = (this.instancesData || []).slice(0, 12)
      const maxInst = instSlice.reduce((m, i) => Math.max(m, Number(i.estimatedMonthlyCost) || 0), 0) || 1
      const instances = instSlice.map(i => ({ ...i, pct: Math.max(2, Math.round((Number(i.estimatedMonthlyCost) || 0) / maxInst * 100)) }))
      // 频繁变更 Top 10
      const freq = (this.freqData || []).slice(0, 10)
      // 账单生命周期 Top 12
      const bills = (this.billsData || []).slice(0, 12).map(b => {
        const cfg = this.billStatusConfig[b.status] || { step: 0 }
        return { linkName: b.linkId || b.linkName, partnerName: b.partnerName, step: cfg.step, charge: b.charge, cost: b.cost }
      })
      return {
        meta: {
          monthLabel: this.instMonthLabel && this.instMonthLabel !== '—' ? this.instMonthLabel : (this.billMonthFilter || ''),
          genAt,
          alertCount: this.alertsData.length,
          partnerCount: (this.partnerNames || []).length,
        },
        kpi: this.kpiData,
        ai: this.aiReport,
        aiRiskText: this.aiRiskText,
        anomalies, instances, freq, bills,
        severityMix, partnerBars,
        steps: this.BILL_STEPS,
      }
    },
    exportPdf() {
      const html = buildReportHtml(this.buildReportPayload())
      const w = window.open('', '_blank')
      if (!w) {
        this.$message ? this.$message.warning('请允许弹出窗口后重试导出') : alert('请允许弹出窗口后重试导出')
        return
      }
      w.document.open()
      w.document.write(html)
      w.document.close()
    },
    async loadAlerts() {
      const res = await ajaxGet({ url: '/stat/alerts/cost_anomaly/' })
      if (res && res.data) this.alertsData = res.data.alerts || []
    },
    async loadFreq() {
      this.freqLoading = true
      try {
        const res = await ajaxGet({ url: '/stat/alerts/frequent_transfer/' })
        if (res && res.data) {
          this.freqData = res.data.payers || []
          this.freqMaxMonth = res.data.maxMonth || null
        }
      } catch (e) {
        console.warn('loadFreq failed:', e.message)
      } finally {
        this.freqLoading = false
      }
    },
    toggleFreqExpand(id) {
      this.freqExpandedIds = { ...this.freqExpandedIds, [id]: !this.freqExpandedIds[id] }
    },
    async loadInstances() {
      this.instLoading = true
      try {
        const params = { page: this.instPage, page_size: this.instPageSize }
        if (this.severityFilter !== 'all') params.severity = this.severityFilter
        if (this.partnerFilter !== 'all') params.partner = this.partnerFilter
        if (this.instanceStatusFilter !== 'all') params.status = this.instanceStatusFilter
        if (this.searchQuery) params.search = this.searchQuery
        const res = await ajaxGet({ url: '/stat/alerts/large_instances/', params })
        if (res && res.data) {
          this.instancesData = res.data.instances || []
          this.instTotal = res.data.total || 0
          this.instMonth = res.data.month || null
          this.instPrevMonth = res.data.prevMonth || null
        }
      } catch (e) {
        console.warn('loadInstances failed:', e.message)
      } finally {
        this.instLoading = false
      }
    },
    _debouncedLoadInstances() {
      clearTimeout(this._instTimer)
      this._instTimer = setTimeout(() => this.loadInstances(), 350)
    },
    async loadPartners() {
      const res = await ajaxGet({ url: '/stat/alerts/partners/' })
      if (res && res.data) this.partnerNames = res.data.partners || []
    },
    async loadPartnerSummary() {
      const res = await ajaxGet({ url: '/stat/alerts/partner_summary/' })
      if (res && res.data) {
        this.partnerSummaryData = res.data.partners || []
        if (this.activeTab === 'by-partner') this.$nextTick(() => this.initPartnerChart())
      }
    },
    async loadBills() {
      this.billLoading = true
      try {
        const params = { page: this.billCurrentPage, page_size: this.billPageSize }
        if (this.billMonthFilter !== 'all') params.bill_month = this.billMonthFilter
        if (this.billStatusFilter !== 'all') params.bill_status = this.billStatusFilter
        if (this.partnerFilter !== 'all') params.partner = this.partnerFilter
        const res = await ajaxGet({ url: '/stat/alerts/bill_lifecycle/', params })
        if (res && res.data) {
          this.billsData = res.data.bills || []
          this.billPipeline = res.data.pipeline || {}
          this.billTotal = res.data.total || 0
          if ((res.data.months || []).length) this.billMonthsServer = res.data.months
        }
      } catch (e) {
        console.warn('loadBills failed:', e.message)
      } finally {
        this.billLoading = false
      }
    },
    toggleExpand(id) {
      const val = !this.expandedIds[id]
      this.expandedIds = { ...this.expandedIds, [id]: val }
    },
    sevLabel(s) { return { critical: '严重', warning: '警告', info: '提示' }[s] || s },
    sevClass(s) { return { critical: 'ca-sev-critical', warning: 'ca-sev-warning', info: 'ca-sev-info' }[s] || '' },
    instStatusLabel(s) { return { new: '新开通', existing: '持续运行' }[s] || s },
    instStatusClass(s) { return { new: 'ca-sev-critical', existing: 'ca-sev-green' }[s] || '' },
    billStatusLabel(s) { return (billStatusConfig[s] || {}).label || s },
    billStatusCls(s) {
      const map = { pending_confirm: 'ca-sev-warning', confirmed: 'ca-sev-info', invoiced: 'ca-sev-purple', paid: 'ca-sev-green', archived: 'ca-sev-gray-light' }
      return map[s] || ''
    },
    billStep(s) { return (billStatusConfig[s] || {}).step || 0 },
    pipelineCount(stepIdx) {
      const stepNames = ['pending_confirm', 'confirmed', 'invoiced', 'paid', 'archived']
      return this.billPipeline[stepNames[stepIdx]] || 0
    },
    nextAction(status) {
      const map = {
        confirmed: { action: 'invoice', label: '标记已开票' },
        invoiced:  { action: 'pay', label: '标记已付款' },
        paid:      { action: 'archive', label: '归档' },
      }
      return map[status] || null
    },
    async doBillAction(bill) {
      const next = this.nextAction(bill.status)
      if (!next) return
      try {
        const res = await ajaxPut({ url: '/bill/updateLifecycle/', params: { ids: String(bill.billSumId), action: next.action } })
        if (res && res.code === 2000) {
          this.$message.success(res.msg || '操作成功')
          this.loadBills()
        } else {
          this.$message.error(res?.msg || '操作失败')
        }
      } catch (e) {
        this.$message.error('操作失败: ' + (e.message || e))
      }
    },
    onStatusChange(bill, newStatus) {
      if (newStatus === bill.status) return
      const oldLabel = this.billStatusLabel(bill.status)
      const newLabel = this.billStatusLabel(newStatus)
      this.$confirm(`确定将 ${bill.id} 的状态从「${oldLabel}」改为「${newLabel}」？`, '修改状态', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(async () => {
        try {
          const res = await ajaxPut({ url: '/bill/updateLifecycle/', params: { ids: String(bill.billSumId), status: newStatus } })
          if (res && res.code === 2000) {
            this.$message.success(`状态已更新为「${newLabel}」`)
            this.loadBills()
          } else {
            this.$message.error(res?.msg || '操作失败')
          }
        } catch (e) {
          this.$message.error('操作失败: ' + (e.message || e))
        }
      }).catch(() => {})
    },
    initPartnerChart() {
      if (this.partnerChartInstance) { this.partnerChartInstance.dispose(); this.partnerChartInstance = null }
      const dom = this.$refs.partnerChartRef
      if (!dom) return
      const ps = Array.isArray(this.partnerSummary) ? this.partnerSummary : []
      this.partnerChartInstance = echarts.init(dom)
      // Guard: empty data → show placeholder, skip series (avoids echarts dataSample reset crash)
      if (!ps.length) {
        this.partnerChartInstance.setOption({
          graphic: { type: 'text', left: 'center', top: 'middle', style: { text: '暂无伙伴告警数据', fontSize: 13, fill: '#71717a' } }
        }, true)
        return
      }
      const names = ps.map(p => (p.name || '').split(' ')[0] || '未分配')
      this.partnerChartInstance.setOption({
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, textStyle: { fontSize: 12 } },
        legend: { data: ['严重', '警告', '提示'], bottom: 0, textStyle: { fontSize: 11, color: '#71717a' }, itemWidth: 8, itemHeight: 8 },
        grid: { left: 40, right: 16, top: 12, bottom: 36 },
        xAxis: { type: 'category', data: names, axisLabel: { fontSize: 11, color: '#6b7280' }, axisLine: { lineStyle: { color: '#e5e7eb' } }, axisTick: { show: false } },
        yAxis: { type: 'value', axisLabel: { fontSize: 11, color: '#6b7280' }, axisLine: { show: false }, axisTick: { show: false }, splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } } },
        series: [
          { name: '严重', type: 'bar', stack: 'a', data: ps.map(p => Number(p.critical) || 0), itemStyle: { color: '#c05c5c' }, barMaxWidth: 32 },
          { name: '警告', type: 'bar', stack: 'a', data: ps.map(p => Number(p.warning) || 0), itemStyle: { color: '#c4894a' }, barMaxWidth: 32 },
          { name: '提示', type: 'bar', stack: 'a', data: ps.map(p => Number(p.info) || 0), itemStyle: { color: '#4a7fc1', borderRadius: [3, 3, 0, 0] }, barMaxWidth: 32 },
        ],
      }, true)
    },
    initChangeChart() {
      if (this.changeChartInstance) { this.changeChartInstance.dispose(); this.changeChartInstance = null }
      const dom = this.$refs.changeChartRef
      if (!dom) return
      const data = Array.isArray(this.changeChartData) ? this.changeChartData : []
      this.changeChartInstance = echarts.init(dom)
      // Guard: empty data → placeholder, skip series
      if (!data.length) {
        this.changeChartInstance.setOption({
          graphic: { type: 'text', left: 'center', top: 'middle', style: { text: '暂无费用变化数据', fontSize: 13, fill: '#71717a' } }
        }, true)
        return
      }
      this.changeChartInstance.setOption({
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, formatter(params) { const p = params[0]; const d = data[p.dataIndex] || {}; return `${d.name || ''}<br/>${d.service || ''}: ${(Number(d.change) || 0).toFixed(1)}%` }, textStyle: { fontSize: 12 } },
        grid: { left: 110, right: 24, top: 12, bottom: 24 },
        xAxis: { type: 'value', axisLabel: { fontSize: 11, color: '#6b7280', formatter: '{value}%' }, axisLine: { lineStyle: { color: '#e5e7eb' } }, axisTick: { show: false }, splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } } },
        yAxis: { type: 'category', data: data.map(d => d.name || ''), axisLabel: { fontSize: 11, color: '#6b7280' }, axisLine: { show: false }, axisTick: { show: false } },
        series: [{ type: 'bar', data: data.map(d => ({ value: Number(d.change) || 0, itemStyle: { color: d.fill || '#9aa5b4', borderRadius: [0, 3, 3, 0] } })), barMaxWidth: 20 }],
      }, true)
    },
    handleResize() {
      if (this.partnerChartInstance) this.partnerChartInstance.resize()
      if (this.changeChartInstance) this.changeChartInstance.resize()
    },
  },
  mounted() {
    this.loadAllData()
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
    if (this.partnerChartInstance) this.partnerChartInstance.dispose()
    if (this.changeChartInstance) this.changeChartInstance.dispose()
  },
}
</script>

<style lang="scss" scoped>
.ca-page { background: #f0f2f5 !important; min-height: 100%; font-family: -apple-system, 'SF Pro Text', 'PingFang SC', 'Microsoft YaHei', sans-serif; letter-spacing: -0.011em; color: #1b1b1f; }
.ca-body { padding: 20px; display: flex; flex-direction: column; gap: 16px; }
.ca-kpi-row { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; }
.ca-kpi-card { background: #fff; border: 1px solid #e4e4e7; border-radius: 8px; padding: 12px 16px; display: flex; align-items: center; gap: 12px; }
.ca-kpi-icon-wrap { width: 36px; height: 36px; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.ca-toolbar { display: flex; align-items: center; margin-bottom: 12px; }
.ca-toolbar-title { font-size: 18px; font-weight: 700; color: #18181b; }
.ca-toolbar-spacer { flex: 1; }
.ca-export-btn { display: inline-flex; align-items: center; gap: 7px; height: 34px; padding: 0 16px; border-radius: 8px; font-size: 13px; font-weight: 600; color: #fff; cursor: pointer; border: none; background: #232f3e; transition: background .15s; }
.ca-export-btn:hover { background: #31465f; }
.ca-kpi-label { font-size: 12px; color: #71717a; }
.ca-kpi-value { font-size: 24px; font-weight: 700; font-variant-numeric: tabular-nums; line-height: 1.2; }
.ca-ai-panel { margin-top: 12px; border-radius: 10px; padding: 1px; background: linear-gradient(120deg, #7d3ff2, #4b6ef5); box-shadow: 0 2px 8px rgba(75,110,245,.12); }
.ca-ai-inner { background: linear-gradient(180deg, #fbfaff, #fff); border-radius: 9px; padding: 14px 18px; }
.ca-ai-head { display: flex; align-items: center; gap: 10px; }
.ca-ai-spark { width: 26px; height: 26px; border-radius: 7px; background: linear-gradient(135deg, #7d3ff2, #4b6ef5); display: grid; place-items: center; color: #fff; flex-shrink: 0; }
.ca-ai-title { font-size: 14px; font-weight: 700; color: #18181b; }
.ca-ai-badge { font-size: 11px; font-weight: 700; color: #7d3ff2; background: #f3edff; border: 1px solid #e0d0ff; padding: 2px 9px; border-radius: 20px; }
.ca-ai-btn { height: 30px; padding: 0 14px; border-radius: 7px; font-size: 13px; font-weight: 600; color: #fff; cursor: pointer; border: none; background: linear-gradient(135deg, #7d3ff2, #4b6ef5); transition: opacity .15s; }
.ca-ai-btn:hover { opacity: .9; }
.ca-ai-btn:disabled { opacity: .55; cursor: default; }
.ca-ai-loading { margin-top: 12px; font-size: 13px; color: #7d3ff2; }
.ca-ai-error { margin-top: 12px; font-size: 13px; color: #dc2626; }
.ca-ai-empty { margin-top: 10px; font-size: 13px; color: #71717a; }
.ca-ai-body { margin-top: 12px; font-size: 13.5px; color: #3f3f46; line-height: 1.7; }
.ca-ai-raw { white-space: pre-wrap; }
.ca-ai-verdicts { display: flex; gap: 10px; margin-top: 12px; flex-wrap: wrap; }
.ca-ai-chip { display: inline-flex; align-items: center; font-size: 12px; font-weight: 600; padding: 5px 12px; border-radius: 7px; background: #fff; border: 1px solid #e4e4e7; color: #3f3f46; }
.ca-ai-risk-high { color: #dc2626; }
.ca-ai-risk-mid { color: #d97706; }
.ca-ai-risk-low { color: #059669; }
.ca-ai-recs { margin: 12px 0 0; padding-left: 20px; font-size: 13px; color: #3f3f46; line-height: 1.8; }
.ca-ai-note { margin-top: 12px; font-size: 12px; color: #a1a1aa; font-style: italic; }
.ca-tabs ::v-deep .el-tabs__header { margin-bottom: 0; }
.ca-tabs ::v-deep .el-tabs__nav-wrap::after { display: none; }
.ca-tabs ::v-deep .el-tabs__item { font-size: 12px; height: 32px; line-height: 32px; padding: 0 14px; color: #71717a; font-weight: 500; }
.ca-tabs ::v-deep .el-tabs__item.is-active { color: #1b1b1f; font-weight: 600; }
.ca-tabs ::v-deep .el-tabs__active-bar { background-color: #2563eb; height: 2px; }
.ca-tab-badge { display: inline-block; font-size: 10px; line-height: 1; padding: 2px 5px; border-radius: 9999px; margin-left: 4px; font-weight: 600; }
.ca-badge-red { background: #fef2f2; color: #b91c1c; }
.ca-badge-orange { background: #fff7ed; color: #c2410c; }
.ca-badge-blue { background: #eff6ff; color: #1d4ed8; }
.ca-filters { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.ca-search-wrap { position: relative; width: 160px; }
.ca-search-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: #71717a; pointer-events: none; }
.ca-search-input { width: 100%; padding: 6px 12px 6px 32px; font-size: 12px; border: 1px solid #e4e4e7; border-radius: 6px; background: #fff; outline: none; color: #1b1b1f; }
.ca-search-input:focus { border-color: #2563eb; box-shadow: 0 0 0 1px rgba(37,99,235,0.15); }
.ca-search-input::placeholder { color: #a1a1aa; }
.ca-filter-select { width: 160px; }
.ca-filter-sm { width: 112px; }
.ca-filters ::v-deep .el-input__inner { font-size: 12px; height: 32px; line-height: 32px; }
.ca-filters ::v-deep .el-input__wrapper { height: 32px; }
.ca-card { background: #fff; border: 1px solid #e4e4e7; border-radius: 8px; overflow: hidden; }
.ca-card-pad { padding: 16px 20px; }
.ca-card-header { padding: 12px 20px; border-bottom: 1px solid #e4e4e7; display: flex; align-items: center; }
.ca-card-header-between { justify-content: space-between; }
.ca-card-title { font-size: 14px; font-weight: 600; color: #1b1b1f; }
.ca-card-subtitle { font-size: 12px; color: #71717a; }
.ca-mb12 { margin-bottom: 12px; }
.ca-mb16 { margin-bottom: 16px; }
.ca-divide-y > * + * { border-top: 1px solid #e4e4e7; }
.ca-empty { padding: 32px 20px; text-align: center; font-size: 14px; color: #71717a; }
.ca-sev-badge { display: inline-flex; align-items: center; gap: 4px; font-size: 10px; font-weight: 500; padding: 2px 6px; border-radius: 4px; border: 1px solid; white-space: nowrap; }
.ca-sev-critical { background: #fef2f2; color: #b91c1c; border-color: #fecaca; }
.ca-sev-warning { background: #fffbeb; color: #b45309; border-color: #fde68a; }
.ca-sev-info { background: #eff6ff; color: #1d4ed8; border-color: #bfdbfe; }
.ca-sev-green { background: #ecfdf5; color: #047857; border-color: #a7f3d0; }
.ca-sev-orange { background: #fff7ed; color: #c2410c; border-color: #fed7aa; }
.ca-sev-purple { background: #faf5ff; color: #7c3aed; border-color: #ddd6fe; }
.ca-sev-gray { background: #f9fafb; color: #4b5563; border-color: #e5e7eb; }
.ca-sev-gray-light { background: #f9fafb; color: #9ca3af; border-color: #e5e7eb; }
.ca-alert-row { width: 100%; display: flex; align-items: center; gap: 12px; padding: 12px 20px; background: none; border: none; cursor: pointer; text-align: left; transition: background 0.15s; font-family: inherit; color: inherit; }
.ca-alert-row:hover { background: rgba(244,244,245,0.5); }
.ca-chevron { flex-shrink: 0; color: #71717a; transition: transform 0.2s; }
.ca-chevron-down { transform: rotate(90deg); }
.ca-alert-info { flex: 1; min-width: 0; display: flex; align-items: center; gap: 8px; }
.ca-alert-link { font-size: 14px; font-weight: 500; color: #1b1b1f; }
.ca-alert-meta { font-size: 12px; color: #71717a; }
.ca-alert-change { display: flex; align-items: center; gap: 4px; font-size: 14px; font-weight: 700; font-variant-numeric: tabular-nums; flex-shrink: 0; }
.ca-text-red { color: #dc2626; }
.ca-text-green { color: #059669; }
.ca-text-primary { color: #2563eb; }
.ca-text-muted { color: #71717a; }
.ca-alert-date { font-size: 12px; color: #71717a; flex-shrink: 0; width: 80px; text-align: right; }
.ca-alert-detail { padding: 0 48px 12px; background: rgba(244,244,245,0.25); }
.ca-detail-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 8px; }
.ca-detail-label { font-size: 10px; color: #71717a; margin-bottom: 2px; }
.ca-detail-value { font-size: 14px; font-weight: 700; font-variant-numeric: tabular-nums; color: #1b1b1f; }
.ca-detail-text { font-weight: 500; }
.ca-detail-desc { font-size: 12px; color: #71717a; line-height: 1.5; }
.ca-table-wrap { overflow-x: auto; }
.ca-table { width: 100%; border-collapse: collapse; font-size: 12px; }
.ca-table thead th { text-align: left; padding: 10px 12px; font-weight: 500; color: #71717a; white-space: nowrap; background: rgba(244,244,245,0.3); border-bottom: 1px solid #e4e4e7; }
.ca-table tbody td { padding: 10px 12px; }
.ca-table-row { border-bottom: 1px solid #e4e4e7; transition: background 0.15s; }
.ca-table-row:last-child { border-bottom: none; }
.ca-table-row:hover { background: rgba(244,244,245,0.5); }
.ca-td-primary { color: #2563eb; font-weight: 500; }
.ca-td-bold { color: #1b1b1f; font-weight: 500; }
.ca-td-muted { color: #71717a; }
.ca-td-red { color: #dc2626; font-weight: 500; }
.ca-font-data { font-variant-numeric: tabular-nums; }
.ca-nowrap { white-space: nowrap; }
.ca-td-truncate { max-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ca-partner-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.ca-partner-row { padding: 12px 20px; display: flex; align-items: center; gap: 12px; }
.ca-partner-info { flex: 1; min-width: 0; }
.ca-partner-name { font-size: 14px; font-weight: 500; color: #1b1b1f; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ca-partner-count { font-size: 12px; color: #71717a; margin-top: 2px; }
.ca-partner-badges { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.ca-legend-row { display: flex; align-items: center; gap: 16px; margin-top: 12px; font-size: 12px; color: #71717a; }
.ca-legend-item { display: flex; align-items: center; gap: 6px; }
.ca-legend-dot { width: 12px; height: 12px; border-radius: 2px; }
.ca-pipeline { display: flex; align-items: center; gap: 4px; }
.ca-pipeline-step { flex: 1; }
.ca-pipeline-box { border-radius: 8px; padding: 10px 12px; text-align: center; }
.ca-pipeline-active { background: rgba(37,99,235,0.08); border: 1px solid rgba(37,99,235,0.15); }
.ca-pipeline-inactive { background: rgba(244,244,245,0.3); border: 1px solid #e4e4e7; }
.ca-pipeline-num { font-size: 18px; font-weight: 700; font-variant-numeric: tabular-nums; line-height: 1.2; }
.ca-pipeline-label { font-size: 10px; color: #71717a; margin-top: 2px; }
.ca-pipeline-arrow { flex-shrink: 0; margin: 0 2px; }
.ca-lifecycle-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
.ca-lifecycle-card { background: #fff; border: 1px solid #e4e4e7; border-radius: 8px; padding: 16px; }
.ca-lc-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 8px; }
.ca-lc-month { font-size: 12px; font-weight: 600; color: #1b1b1f; }
.ca-lc-partner { font-size: 11px; color: #71717a; }
.ca-lc-amounts { display: flex; align-items: flex-end; justify-content: space-between; margin-bottom: 12px; }
.ca-lc-amount { font-size: 16px; font-weight: 700; font-variant-numeric: tabular-nums; color: #1b1b1f; }
.ca-lc-due { font-size: 12px; font-weight: 500; color: #1b1b1f; }
.ca-text-right { text-align: right; }
.ca-lc-progress { display: flex; align-items: flex-start; gap: 2px; }
.ca-lc-step { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 2px; }
.ca-lc-bar { width: 100%; height: 4px; border-radius: 9999px; }
.ca-lc-bar-done { background: #2563eb; }
.ca-lc-bar-current { background: rgba(37,99,235,0.5); }
.ca-lc-bar-pending { background: #e4e4e7; }
.ca-lc-step-label { font-size: 9px; color: #2563eb; font-weight: 500; text-align: center; line-height: 1.2; }
.ca-lc-notes { margin-top: 8px; font-size: 11px; color: #71717a; border-top: 1px solid #e4e4e7; padding-top: 8px; }
.ca-lc-action { margin-top: 10px; }
.ca-action-btn {
  background: #0073bb;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 4px 12px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
}
.ca-action-btn:hover { background: #005a94; }
.ca-pagination { display: flex; justify-content: flex-end; padding: 12px 16px 8px; }
.ca-status-clickable { cursor: pointer; user-select: none; }
.ca-status-clickable:hover { opacity: 0.8; filter: brightness(0.95); }
.ca-sev-orange { background: rgba(249, 115, 22, 0.1); color: #ea580c; }
</style>
